import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem from "@insite/mobius/GridItem/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    addToCartButtonGridStyle,
    qtyGridStyle,
} from "@theStellarBlueprint/Common/Utilities/productTileComponentStyle";
import ProductAddToCartButton from "@theStellarBlueprint/Overrides/Components/ProductAddToCartButton";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    addToCartButtons: InjectableCss;
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    uomQtyWrapper?: InjectableCss;
    qtyOrderedWrapper?: InjectableCss;
    uomSelectWrapper?: InjectableCss;
    discontinuedInfoGridItem?: InjectableCss;
    discontinuedInfoMessage?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            background-color: #fafafa;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 0px;
                flex-direction: row;
                label {
                    flex-basis: 28%;
                    justify-content: center;
                    background: #fff;
                    display: flex;
                    align-items: center;
                    padding: 4px;
                    border: 1px solid #d9d9d9;
                    border-right: none;
                    font-size: 16px;
                    &:after {
                        content: ":";
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                flex-basis: 26%;
                            `,
                            css`
                                flex-basis: 20%;
                            `,
                            css`
                                flex-basis: 38%;
                            `,
                            css`
                                flex-basis: 38%;
                            `,
                            null,
                        ])}
                }
                div[class^="FormInputWrapper"] {
                    margin-top: 0 !important;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
        `,
    },
    addToCartButton: {
        variant: "primary",
        css: addToCartButtonGridStyle,
    },
    addToCartButtons: {
        css: css`
            cursor: not-allowed;
            pointer-events: none;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            // formField: qtyGridStyle,
            formField: css`
                margin-right: 10px;
                width: 18%;
                min-width: 3rem;
            `,
            inputSelect: css`
                width: 100%;
                box-sizing: border-box;
                font-family: inherit;
                font-size: 15px;
                outline: 0px;
                appearance: none;
                color: inherit;
                border: 1px solid rgb(204, 204, 204);
                border-radius: 0px;
                padding: 0px 10px;
                text-align: center;
                height: 40px;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            display: flex;
            & svg {
                color: #a8a8a8;
            }
            & button {
                &:hover,
                &:focus {
                    svg {
                        fill: #59b46c;
                    }
                    &:hover {
                        background: #69a342;
                        border: 2px solid #69a342;
                        span {
                            color: #ffffff !important;
                        }
                    }
                    outline: none;
                }
                cursor: pointer;
                font-family: inherit;
                height: 40px;
                line-height: 1;
                background: transparent;
                border: 2px solid #91a1a3;
                color: #363636;
                width: 100%;
                padding: 0 12px 0 12px;
                text-transform: uppercase;
                margin: 0px 15px 10px 15px;
                display: flex;
                justify-content: center;
                span {
                    color: #91a2a4 !important;
                    font-weight: 700;
                }
            }
        `,
    },
    uomQtyWrapper: {
        css: css`
            display: flex;
            margin: 0px 15px;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                ])}
        `,
    },
    uomSelectWrapper: {
        css: css`
            flex-basis: 100%;
        `,
    },
    qtyOrderedWrapper: {
        css: css`
            display: flex;
            margin: 8px 15px;
            width: 10%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    null,
                ])}
        `,
    },
    discontinuedInfoGridItem: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
        `,
    },
    discontinuedInfoMessage: {
        css: css`
            color: red;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({ productContext, productSettings, showPrice, showAddToList }) => {
    const isButtonDisabled =
        productContext.productInfo.inventory?.qtyOnHand === 0 &&
        productContext.productInfo.inventory?.additionalResults?.isDiscontinued?.toUpperCase() === "TRUE";

    const isDiscontinuedMessage =
        productContext.productInfo.inventory?.additionalResults?.isDiscontinued !== null &&
        productContext.productInfo.inventory?.additionalResults?.isDiscontinued?.toUpperCase() === "TRUE";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper>
                {showPrice && (
                    <>
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.price}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.uomQtyWrapper}>
                <StyledWrapper {...styles.uomSelectWrapper}>
                    <ProductUnitOfMeasureSelect labelOverride="UOM" extendedStyles={styles.unitOfMeasureSelect} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartWrapper}>
                <StyledWrapper {...styles.qtyOrderedWrapper}>
                    <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                </StyledWrapper>
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                    isButtonDisabled={isButtonDisabled}
                />
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
            {isDiscontinuedMessage && (
                <GridItem {...styles.discontinuedInfoGridItem}>
                    <Typography {...styles.discontinuedInfoMessage} as="p">
                        {siteMessage("DiscontinuedInfo_Message")}
                    </Typography>
                </GridItem>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
